import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button as MButton } from "@mui/material";
import { ArrowBackIos } from "@mui/icons-material";
import { Rating, Typography, Card } from "@material-tailwind/react";
import moment from "moment";

// Local imports
import { Button } from "../../components/Button";
import useAuth from "../../hooks/useAuth";
import { appraisalState, updateMessage } from "./appraisalSlice";
import {
  createAppraisal,
  getEmployeeAppraisalList,
  getUserAssignedAppraisalDetails,
  submitFinalAppraisal,
  updateUserAdminAppraisal,
} from "../../api/appraisalApi";
import {
  getRatingName,
  getRatingNameValue,
  ROLES,
  STATUS,
} from "../../uitls/Utils";
import { showNotification } from "../../components/Notification";
const AddUpdateManagerSelfAppraisal = () => {
  const { auth } = useAuth();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [selectedTemplate, setSelectedTemplate] = useState({});
  const [managerFeedback, setManagerFeedback] = useState({});
  const [finalReviewComment, setFinalReviewComment] = useState("");
  const [rated, setRated] = useState(1);
  const [startDate, setStartDate] = useState(new Date());

  const { selectedAppraisal, employeeAppraisals, status, loading, error } =
    useSelector((state) => state.appraisalState);
  const USER = "User";
  const MANAGER = "Manager";
  const [searchParams] = useSearchParams();

  const id = searchParams.get("id");
  useEffect(() => {
    if (id) {
      dispatch(getUserAssignedAppraisalDetails(id));
    }
  }, [searchParams, dispatch, id]);

  useEffect(() => {
    if (auth?.user?.id && selectedAppraisal) {
      const payload = {
        userId: auth?.user?.id,
        appraisalCreatedFor: selectedAppraisal?.submittedAppraisalId,
        loginType: "MANAGER",
      };
      dispatch(getEmployeeAppraisalList(payload));
    }
  }, [auth, dispatch, selectedAppraisal]);

  useEffect(() => {
    setFinalReviewComment(selectedAppraisal?.remark || "");
    setRated(getRatingNameValue(selectedAppraisal?.performanceRating || 0));
    if (selectedAppraisal?.appraisalDiscussionDate) {
      setStartDate(new Date(selectedAppraisal?.appraisalDiscussionDate));
    } else {
      setStartDate(new Date());
    }
  }, [
    employeeAppraisals,
    selectedAppraisal?.appraisalDiscussionDate,
    selectedAppraisal?.performanceRating,
    selectedAppraisal?.remark,
  ]);

  useEffect(() => {
    if (employeeAppraisals.length === 0) {
      setSelectedTemplate(selectedAppraisal?.appraisalTemplate);
      setManagerFeedback([]);
    }
    if (employeeAppraisals?.length === 2) {
      setSelectedTemplate(prepareDataToDisplay(getDataByRole(USER)));
      setManagerFeedback(prepareDataToDisplay(getDataByRole(MANAGER)));
    } else if (employeeAppraisals?.length === 1 && auth?.roleId === ROLES.mgr) {
      const data = getDataByRole(USER);
      setSelectedTemplate(prepareDataToDisplay(data));
      setManagerFeedback(selectedAppraisal?.appraisalTemplate);
    }
  }, [auth?.roleId, employeeAppraisals, JSON.stringify(selectedAppraisal)]);

  const getDataByRole = (submittedBy) => {
    return employeeAppraisals?.filter((e) => e.submittedBy === submittedBy)[0];
  };

  const prepareDataToDisplay = (data) => {
    let finalData = {
      ...data?.appraisalTemplate,
      additionalComments: data?.additionalComments,
      appraisalQuestions: (
        data?.appraisalTemplate?.appraisalQuestions || []
      ).map((question) => {
        const filteredUserAns = data.appraisalQuestionAnswer?.find(
          (userAnsData) => userAnsData?.questionId === question.id
        );
        return {
          ...question,
          comment: filteredUserAns?.comment || "",
          selectedOptionId: filteredUserAns?.selectedOptionId || null,
        };
      }),
    };
    return finalData;
  };

  const stateOfAppraisal = useSelector(appraisalState);

  const getUserId = () => {
    if (auth?.user?.managerId) {
      return auth?.user?.id;
    } else {
      return getDataByRole(USER)?.user?.id;
    }
  };

  const getSubmittedBy = () => {
    if (auth.user?.managerId) {
      return USER;
    } else {
      return MANAGER;
    }
  };

  const getActionType = (actionType) => {
    if (actionType === 1) {
      return "Draft";
    } else if (actionType === 2) {
      return "Submit";
    } else if (actionType === 4) {
      return "Change Request";
    } else {
      return "Discussion";
    }
  };
  const handleSubmit = (action) => {
    let allData = null;
    if (auth?.user?.managerId) {
      allData = selectedTemplate;
    } else {
      allData = managerFeedback;
    }
    const updatedItems = allData.appraisalQuestions.map((question) => {
      const data = {
        questionId: question?.id,
        id: getSavedQuestionAnsRowData(question?.id),
        selectedOptionId:
          question?.selectedOptionId && question?.selectedOptionId !== "null"
            ? question?.selectedOptionId
            : null,
        comment: question?.comment || null,
      };
      return data;
    });
    const date = new Date(startDate);
    const isoDate = date.toISOString();

    const payload = {
      appraisalQuestionAnswer: updatedItems,
      businessUnitId: allData?.businessUnit,
      additionalComments: allData?.additionalComments || "",
      templateId: allData?.id,
      submittedBy: getSubmittedBy(),
      userId: getUserId(),
      appraisalCreatedFor: getDataByRole(USER)?.appraisalCreatedFor || null, //,
      actionType: getActionType(action),
      userAssignedAppraisalId: selectedAppraisal?.id,
      sendChangeRequest: managerFeedback?.sendChangeRequest ? 1 : 0,
      finalReviewComments:
        auth?.roleId === ROLES.admin ? finalReviewComment : "",
      performanceRating: rated,
      appraisalDiscussionDate: isoDate,
    };
    if (action !== 1) {
      if (!validateFormData()) {
        return;
      }
    }

    const actionType = searchParams.get("action");

    if (actionType === "View") {
      let appraisalId = null;
      if (auth?.user?.managerId) {
        appraisalId = getDataByRole(USER)?.id;
      } else {
        appraisalId = getDataByRole(MANAGER)?.id;
      }
      if (appraisalId) {
        const updatePayload = {
          data: payload,
          id: appraisalId,
        };
        dispatch(updateUserAdminAppraisal(updatePayload));
      }
      return;
    }
    if (auth.roleId === ROLES.admin) {
      dispatch(submitFinalAppraisal(payload));
      return;
    }
    dispatch(
      createAppraisal({
        editStatus: false,
        data: payload,
      })
    );
  };

  const getSavedQuestionAnsRowData = (questionId) => {
    const actionType = searchParams.get("action");
    let data = null;
    if (actionType === "View") {
      if (auth?.user?.managerId) {
        data = getDataByRole(USER);
      } else {
        data = getDataByRole(MANAGER);
      }
      return (
        data?.appraisalQuestionAnswer?.find((e) => e.questionId === questionId)
          ?.id || null
      );
    }
  };

  const updateQuestionAnswer = (qIndex, updateAnsObject) => {
    const updatedTemplate = {
      ...selectedTemplate,
      appraisalQuestions: [
        ...selectedTemplate.appraisalQuestions.slice(0, qIndex),
        {
          ...selectedTemplate.appraisalQuestions[qIndex],
          ...updateAnsObject,
        },
        ...selectedTemplate.appraisalQuestions.slice(qIndex + 1),
      ],
    };
    setSelectedTemplate(updatedTemplate);
  };

  useEffect(() => {
    if (status === "succeeded" && loading === false && error) {
      showNotification("success", error);
      goBack();
    }
    if (status === "failed" && loading === false && error) {
      showNotification("error", error);
    }

    setTimeout(() => {
      dispatch(updateMessage());
    }, 500);
  }, [status, error, loading]);
  const goBack = () => {
    if (auth?.roleId === ROLES.mgr) {
      navigate("/mgr/manager-self-appraisal");
    } else if (auth?.roleId === ROLES.admin) {
      navigate("/admin/assign-template");
    }
  };
  const canShowButtons = () => {
    const {
      NOT_STARTED,
      COMPLETED,
      IN_PROGRESS,
      UNDER_REVIEW,
      CHANGE_REQUEST,
    } = STATUS;
    if (
      [COMPLETED, UNDER_REVIEW].includes(
        selectedAppraisal?.currentAppraisalStatus
      ) &&
      !auth.user?.managerId
    ) {
      return true;
    } else if (
      [NOT_STARTED, IN_PROGRESS, CHANGE_REQUEST].includes(
        selectedAppraisal?.currentAppraisalStatus
      ) &&
      auth.user?.managerId
    ) {
      return true;
    } else return false;
  };

  const showDraftButton = () => canShowButtons();
  const showSubmitButton = () => canShowButtons();

  const validateFormData = () => {
    let allData = null;
    let isValid = true;
    if (auth?.user?.managerId) {
      allData = selectedTemplate;
    } else {
      allData = managerFeedback;
    }
    const updatedItems = allData.appraisalQuestions.map((question) => {
      let data = {};
      if (
        question?.selectedOptionId === "" ||
        question?.selectedOptionId === "null" ||
        !question?.selectedOptionId
      ) {
        isValid = false;
        data = {
          ...question,
          errorMessage: "Please select an option",
        };
      } else {
        data = { ...question };
      }

      return data;
    });

    let additionalCommentsError = "";
    if (
      [STATUS.COMPLETED, STATUS.UNDER_REVIEW].includes(
        selectedAppraisal?.currentAppraisalStatus
      ) &&
      (!allData?.additionalComments ||
        allData?.additionalComments?.trim()?.length === 0)
    ) {
      additionalCommentsError = "Please enter additiona comments";
      isValid = false;
    }
    const finalData = {
      ...allData,
      appraisalQuestions: updatedItems,
      additionalCommentsError: additionalCommentsError,
    };

    if (auth?.user?.managerId) {
      setSelectedTemplate(finalData);
    } else {
      setManagerFeedback(finalData);
    }

    return isValid;
  };

  const showDiscussion = (isReadOnly) => {
    return (
      <div className=" m-4">
        <p className="mt-4">
          Manager's availability for appraisal discussion
          {!isReadOnly ? <span className="text-red-500">*</span> : ""}
        </p>
        <div className="border-[1px] border-[#ddd] !w-fit">
          {moment(startDate).format("MM/DD/YYYY h:mm A")}
        </div>
      </div>
    );
  };

  const showRating = (isChangeAble) => {
    return (
      <>
        <p className="text-[#333333] mt-2 ml-5 mb-1">
          Overall Performance Rating:
        </p>
        <div className="flex items-center gap-2 font-bold text-blue-gray-500">
          <Rating
            className="ml-5"
            value={rated}
            onChange={(value) => setRated(value)}
            readonly={isChangeAble}
          />
          <Typography
            color="blue-gray"
            className="font-medium text-blue-gray-500"
          >
            {getRatingName(rated)}
          </Typography>
        </div>
      </>
    );
  };

  return (
    <div className="rounded-sm border border-stroke bg-white shadow-default">
      <div className="flex w-full justify-center items-center bg-primary">
        <button
          onClick={goBack}
          className="flex w-32 pl-3 text-white cursor-pointer bg-transparent border-none outline-none"
          aria-label="Go back"
        >
          <ArrowBackIos /> Go Back
        </button>
        <h1 className="w-full lg:text-title-sm text-title-xsm text-center font-semibold text-white bg-primary dark:text-white py-3">
          {selectedTemplate?.templateTitle}
        </h1>
        <div className="w-34" />
      </div>
      <div className="grid grid-cols-2 gap-3 border-c-2 h-full">
        {/* employee submission appraisal */}
        <Card>
          <div className=" !bg-white">
            {selectedTemplate?.templateTitle && (
              <div className="rounded-sm p-4 ">
                <h2 className="text-xl font-normal text-center mt-2 mb-2 text-white bg-gradient-to-r from-[#0b3a5c] to-[#135e96] rounded-lg px-4 py-3 shadow">
                  Employee Appraisal Review
                </h2>
                <div className="">
                  <div>
                    {selectedTemplate.appraisalQuestions?.map(
                      (question, qIndex) => (
                        <div key={question.id} className="mb-3">
                          <div className="font-normal text-base ml-2">
                            {`${qIndex + 1}. ${question.questionTitle}`}
                          </div>
                          <div className="ml-8">
                            {question.questionSubTitle}
                          </div>
                          <div className="mt-4 ml-12">
                            {question.questionOptions?.map(
                              (optionObj, optionIndex) => {
                                const optionValue = optionObj?.["optionTitle"];
                                return (
                                  <div
                                    key={optionObj.id}
                                    className="mb-5 flex align-middle content-center"
                                  >
                                    <span className="font-medium mr-2">
                                      {String.fromCharCode(65 + optionIndex)}.
                                    </span>
                                    {question.optionType === 1 ? (
                                      <input
                                        type="checkbox"
                                        name={`employe-checkbox-option${
                                          qIndex + 1
                                        }-${optionIndex + 1}`}
                                        className="mr-2"
                                        checked={
                                          question.selectedOptionId ===
                                          optionObj?.id
                                        }
                                        onChange={(e) => {
                                          const jsonAns = {
                                            selectedOptionId: optionObj?.id,
                                            errorMessage: "",
                                          };
                                          updateQuestionAnswer(qIndex, jsonAns);
                                        }}
                                      />
                                    ) : (
                                      <input
                                        readOnly={!showSubmitButton()}
                                        type="radio"
                                        className="mr-2 w-5 h-5 cursor-pointer"
                                        name={`employee${question.questionTitle}`}
                                        checked={
                                          question.selectedOptionId ===
                                          optionObj?.id
                                        }
                                        onChange={(e) => {
                                          if (!showSubmitButton()) return;
                                          const jsonAns = {
                                            selectedOptionId: optionObj?.id,
                                            errorMessage: "",
                                          };
                                          updateQuestionAnswer(qIndex, jsonAns);
                                        }}
                                      />
                                    )}
                                    {optionValue}
                                  </div>
                                );
                              }
                            )}
                          </div>
                          <label
                            className={`text-xs mb-2 -mt-2 ml-11 h-[1em] flex transition-opacity duration-150 relative text-[#FF0000] 
                             `}
                          >
                            {question?.errorMessage}
                          </label>
                          <div className="">
                            <p>Comments</p>
                            <textarea
                              key={`employee-comment-${question.id}`}
                              readOnly={!showSubmitButton()}
                              className="block p-2.5 w-full h-12 text-sm text-gray-900 bg-gray-50 rounded-lg border-[1px] border-stroke focus:ring-primary focus:border-red-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                              placeholder="Write your comments..."
                              value={question?.comment || ""} // Ensure it defaults to empty string if undefined
                              onChange={(e) => {
                                const updatedComment = e.target.value;
                                const jsonAns = { comment: updatedComment };
                                updateQuestionAnswer(qIndex, jsonAns); // Ensure this updates state properly
                              }}
                            ></textarea>
                          </div>
                        </div>
                      )
                    )}
                    <div className="mt-10 border-stroke">
                      <p className="mt-5">Additional Comments</p>
                      <textarea
                        readOnly={!showSubmitButton()}
                        className="block p-2.5 w-full h-12 text-sm text-gray-900 bg-gray-50 rounded-lg border-[1px] border-stroke focus:ring-primary focus:border-red-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                        placeholder="Write your comments.."
                        value={selectedTemplate?.additionalComments || ""}
                        onChange={(e) => {
                          setSelectedTemplate({
                            ...selectedTemplate,
                            additionalComments: e.target.value,
                          });
                        }}
                      ></textarea>
                    </div>
                  </div>
                </div>

                <div className="flex justify-center mt-4 pb-2">
                  {showDraftButton() && (
                    <MButton
                      disabled={stateOfAppraisal?.loading}
                      variant="outlined"
                      onClick={() => handleSubmit(1)}
                      className="mr-5 !capitalize"
                    >
                      Draft
                    </MButton>
                  )}

                  <div className="w-10" />
                  {showSubmitButton() && (
                    <Button
                      disabled={stateOfAppraisal?.loading}
                      value={`Submit`}
                      handleClick={() => handleSubmit(2)}
                    />
                  )}
                </div>
              </div>
            )}
          </div>
        </Card>
        {/* manager Feedback */}
        <Card>
          <div className=" !bg-white">
            {managerFeedback?.templateTitle && (
              <div className="rounded-sm p-4">
                <h2 className="text-xl font-normal text-center mt-2 mb-2 text-white bg-gradient-to-r from-[#0b3a5c] to-[#135e96] rounded-lg px-4 py-3 shadow">
                  Manager Appraisal Review
                </h2>
                <div className="">
                  <div>
                    {managerFeedback.appraisalQuestions?.map(
                      (question, qIndex) => (
                        <div key={question.id} className="mb-3">
                          <div className="font-normal text-base ml-2">
                            {`${qIndex + 1}. ${question.questionTitle}`}
                          </div>
                          <div className="ml-8">
                            {question.questionSubTitle}
                          </div>
                          <div className="mt-4 ml-12">
                            {question.questionOptions?.map(
                              (optionObj, optionIndex) => {
                                const optionValue = optionObj?.["optionTitle"];
                                return (
                                  <div
                                    key={optionObj.id}
                                    className="mb-5 flex align-middle content-center"
                                  >
                                    <span className="font-medium mr-2">
                                      {String.fromCharCode(65 + optionIndex)}.
                                    </span>
                                    {question.optionType === 1 ? (
                                      <input
                                        type="checkbox"
                                        readOnly={true}
                                        name={`manager-checkbox-option${
                                          qIndex + 1
                                        }-${optionIndex + 1}`}
                                        className="mr-2"
                                        checked={
                                          question.selectedOptionId ===
                                          optionObj?.id
                                        }
                                        onChange={(e) => {}}
                                      />
                                    ) : (
                                      <input
                                        readOnly={true}
                                        type="radio"
                                        className="mr-2 w-5 h-5 cursor-pointer"
                                        name={`manager${question.questionTitle}`}
                                        checked={
                                          question.selectedOptionId ===
                                          optionObj?.id
                                        }
                                        onChange={(e) => {}}
                                      />
                                    )}
                                    {optionValue}
                                  </div>
                                );
                              }
                            )}
                          </div>
                          <label
                            className={`text-xs mb-2 -mt-2 ml-11 h-[1em] flex transition-opacity duration-150 relative text-[#FF0000] 
                             `}
                          >
                            {question?.errorMessage}
                          </label>
                          <div className="">
                            <p>Comments</p>
                            <textarea
                              key={`manager-comment-${question.id}`}
                              className="block p-2.5 w-full h-12 text-sm text-gray-900 bg-gray-50 rounded-lg border-[1px] border-stroke focus:ring-primary focus:border-red-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                              placeholder="Write your comments.."
                              value={question?.comment}
                              readOnly={true}
                              onChange={(e) => {}}
                            ></textarea>
                          </div>
                        </div>
                      )
                    )}
                    <div className="mt-10 border-stroke ">
                      <p className="mt-5">Additional Comments</p>
                      <textarea
                        className="block p-2.5 w-full h-12 text-sm text-gray-900 bg-gray-50 rounded-lg border-[1px] border-stroke focus:ring-primary focus:border-red-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                        placeholder="Write your comments.."
                        value={managerFeedback?.additionalComments || ""}
                        readOnly={true}
                        onChange={(e) => {}}
                      ></textarea>
                      <label
                        className={`text-xs mb-2  h-[1em] flex transition-opacity duration-150 relative text-[#FF0000] 
                        `}
                      >
                        {managerFeedback?.additionalCommentsError}
                      </label>
                    </div>
                  </div>
                </div>
                {["NEED_DISCUSSION"].includes(
                  selectedAppraisal?.currentAppraisalStatus
                ) && showDiscussion(true)}

                <div>{showRating(true)}</div>
              </div>
            )}
          </div>
        </Card>
      </div>
      {[STATUS.FINAL_REVIEWED].includes(
        selectedAppraisal?.currentAppraisalStatus
      ) && (
        <>
          <hr className="mt-4 border-[#DDD]" />
          <div className="admin-feeback-section p-6">
            <div className="  border-stroke ">
              <p className="mt-5">Final Review Comment</p>
              <textarea
                className="block p-2.5 w-full h-12 text-sm text-gray-900 bg-gray-50 rounded-lg border-[1px] border-stroke focus:ring-primary focus:border-red-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                placeholder="Write your comments.."
                value={finalReviewComment || ""}
                readOnly={true}
                onChange={(e) => {}}
              ></textarea>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
export default AddUpdateManagerSelfAppraisal;
